import moment from 'moment';

// Shared by the hashtag Chat and Insights PDF downloads (bug915).

const PDF_TIMESTAMP_FORMAT = 'MMMM D, YYYY [at] h:mm A';
const PDF_FILENAME_DATE_FORMAT = 'dddd, MMMM Do YYYY';
const FILENAME_UNSAFE_CHARS = /[\\/:*?"<>|]/g;
const MESSAGE_BLOCK_CLASS = 'uc-pdf-msg';
const HEADER_CLASS = 'uc-pdf-header';
const HOST_CLASS = 'uc-pdf-export-host';

// A chunk is one html2canvas pass; 7000 CSS px x scale 2 stays under Chrome's canvas cap.
export const MAX_CHUNK_CSS_PX = 7000;

// Absolute, viewer-local timestamp; an empty string for a missing or invalid value.
export const formatPdfTimestamp = (value) => {
  if (!value) return '';
  const parsed = moment(new Date(value));
  return parsed.isValid() ? parsed.format(PDF_TIMESTAMP_FORMAT) : '';
};

// "<dddd, MMMM Do YYYY> - #<hashtag> <suffix>.pdf", same convention as Download Report.
export const buildPdfFilename = (hashtagLabel, suffix) => {
  const safeLabel = String(hashtagLabel || '').replace(FILENAME_UNSAFE_CHARS, '').trim();
  return `${moment().format(PDF_FILENAME_DATE_FORMAT)} - #${safeLabel || 'hashtag'} ${suffix}.pdf`;
};

export const waitForNextFrame = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });

// Same options as Download Report (letter, 0.5in/0.2in margins, scale 2, jpeg 0.98).
const buildPdfOptions = (filename) => ({
  margin: [0.5, 0.2, 0.5, 0.2],
  filename,
  image: { type: 'jpeg', quality: 0.98 },
  html2canvas: { scale: 2, useCORS: true, scrollX: 0, scrollY: 0 },
  jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
  pagebreak: { mode: ['css', 'legacy'], avoid: `.${MESSAGE_BLOCK_CLASS}` },
});

// One node is the plain Report call; N nodes are appended to a single jsPDF, one canvas each.
export const exportNodesToPdf = async (nodes, filename) => {
  if (typeof window === 'undefined' || typeof window.html2pdf !== 'function') {
    throw new Error('html2pdf is not loaded');
  }
  if (!nodes || nodes.length === 0) {
    throw new Error('Nothing to export');
  }
  const opts = buildPdfOptions(filename);

  if (nodes.length === 1) {
    await window.html2pdf().from(nodes[0]).set(opts).save();
    return;
  }

  let worker = window.html2pdf().set(opts).from(nodes[0]).toContainer().toCanvas().toPdf();
  for (let i = 1; i < nodes.length; i += 1) {
    worker = worker
      .get('pdf')
      .then((pdf) => {
        pdf.addPage();
      })
      .from(nodes[i])
      .toContainer()
      .toCanvas()
      .toPdf();
  }
  await worker.save(filename);
};

const getOuterHeight = (el) => {
  const style = window.getComputedStyle(el);
  return el.offsetHeight + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
};

// Greedy grouping into index lists; the header counts toward the first chunk only.
export const groupBlocksIntoChunks = (headerHeight, blockHeights, maxHeight) => {
  const chunks = [];
  let current = [];
  let currentHeight = headerHeight;
  blockHeights.forEach((height, index) => {
    if (current.length > 0 && currentHeight + height > maxHeight) {
      chunks.push(current);
      current = [];
      currentHeight = 0;
    }
    current.push(index);
    currentHeight += height;
  });
  if (current.length > 0) chunks.push(current);
  return chunks;
};

// Splits a mounted .uc-pdf-doc into height-capped chunk nodes; call dispose() when done.
export const splitIntoPdfChunks = (docEl) => {
  const noop = () => {};
  const blocks = Array.prototype.filter.call(docEl.children, (el) =>
    el.classList.contains(MESSAGE_BLOCK_CLASS)
  );
  if (blocks.length === 0) {
    return { nodes: [docEl], dispose: noop };
  }

  const header = docEl.querySelector(`.${HEADER_CLASS}`);
  const headerHeight = header ? getOuterHeight(header) : 0;
  const chunks = groupBlocksIntoChunks(headerHeight, blocks.map(getOuterHeight), MAX_CHUNK_CSS_PX);
  if (chunks.length <= 1) {
    return { nodes: [docEl], dispose: noop };
  }

  const host = document.createElement('div');
  host.className = HOST_CLASS;
  const nodes = chunks.map((indices, chunkIndex) => {
    const wrapper = docEl.cloneNode(false);
    if (chunkIndex === 0 && header) {
      wrapper.appendChild(header.cloneNode(true));
    }
    indices.forEach((blockIndex) => {
      wrapper.appendChild(blocks[blockIndex].cloneNode(true));
    });
    host.appendChild(wrapper);
    return wrapper;
  });
  document.body.appendChild(host);

  return {
    nodes,
    dispose: () => {
      if (host.parentNode) {
        host.parentNode.removeChild(host);
      }
    },
  };
};
